import React, { useEffect, useState } from 'react';
import './css/Blog.css';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format, parseISO } from 'date-fns';
import { plainText } from '../utils/html';

// Keep these in sync with the "/BlogPage" entry in SeoPageRenderer.cs
const PAGE_URL = "https://www.dysband.com/BlogPage";
const PAGE_TITLE = "Captains Log | Dirty Yachtin' Scoundrels";
const PAGE_DESCRIPTION =
    "Stay updated with the yacht rock band Dirty Yachtin' Scoundrels: band news, yacht rock history, behind-the-scenes stories, and smooth music insights.";
const PAGE_IMAGE =
    "https://www.dysband.com/media/yacht_rock_band_dirty_yachtin_scoundrels_live.webp";

const sortNewestFirst = (posts) =>
    [...posts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

const Blog = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch('/api/blog')
            .then((res) => {
                if (!res.ok) throw new Error('Failed to load posts');
                return res.json();
            })
            .then((data) => {
                // Only show published posts (the old version filtered but then ignored the result)
                const visiblePosts = data.filter(
                    (post) => post.visible === 1 || post.visible === true
                );
                setPosts(sortNewestFirst(visiblePosts));
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    return (
        <div className="blog-container">
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={PAGE_DESCRIPTION} />
                <link rel="canonical" href={PAGE_URL} />

                <meta property="og:title" content={PAGE_TITLE} />
                <meta property="og:description" content={PAGE_DESCRIPTION} />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Dirty Yachtin' Scoundrels" />
                <meta property="og:image" content={PAGE_IMAGE} />
                <meta property="og:image:alt" content="Dirty Yachtin' Scoundrels performing live on stage." />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={PAGE_TITLE} />
                <meta name="twitter:description" content={PAGE_DESCRIPTION} />
                <meta name="twitter:image" content={PAGE_IMAGE} />
            </Helmet>

            <h1 className="blog-title">Captain’s Log</h1>

            {loading && <p>Loading posts...</p>}
            {error && <p style={{ color: 'red' }}>Error: {error}</p>}

            {!loading && !error && posts.length === 0 && <p>No posts available.</p>}
            <div className="blog-posts-flex">
                {!loading && !error && posts.map((post) => {
                    const rawDate = post.createdAt || post.date;
                    const parsedDate = rawDate ? parseISO(rawDate.replace(' ', 'T')) : null;
                    const formattedDate = parsedDate ? format(parsedDate, "MMMM d, yyyy") : "Unknown date";

                    // Handle imageUrl field (API probably returns camelCase)
                    const imageUrl = post.imageUrl || post.ImageUrl || post.ImageURL || '';
                    const content = post.content || '';

                    return (
                        <article key={post.slug} className="blog-post">
                            {imageUrl && (
                                <img
                                    src={imageUrl}
                                    alt={post.title || "Blog post image"}
                                    className="blog-post-image"
                                />
                            )}
                            <h2>{post.title}</h2>
                            <div className="author">Updated On {formattedDate} By {post.author}</div>

                            {/* Plain-text preview: cutting HTML at a fixed length can break tags */}
                            <p>{plainText(content, 250)}</p>
                            <Link to={`/BlogPage/${post.slug}`} className="read-more-link">
                                Read More
                            </Link>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default Blog;